import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import type { ParsedSparklink } from '@arthelper/shared/sparklink';

import { resolveFlyoutImage } from '~/lib/arty-flyout/image';
import { markAutoSubmitted, sparklinkFileParts, wasAutoSubmitted } from '~/lib/arty-flyout/sparklink';

export type Landing = { parsed: ParsedSparklink; key: string; chatId: string };

function restoreDraft(chat: any, message: string) {
  chat.setInput((current: string) => current || message);
}

/**
 * Sends a sparklink's opening message once, into the fresh chat the landing opened. A piece the
 * link names but that never resolves sends nothing (no "critique this" with no piece); its prompt
 * waits in the composer instead. A landing already sent this session does the same.
 *
 * The landing stays set until the run is over, so the effect's abort signal fires only when a
 * newer open, a new chat or a close supersedes it. A superseded run restores nothing: the
 * composer belongs to that newer chat. `onDone` clears the landing afterwards.
 */
export function useLandingSubmit({
  landing,
  chat,
  onResolved,
  onDone,
}: {
  landing: Landing | null;
  chat: any;
  onResolved: any;
  onDone: any;
}) {
  const apolloClient = useApolloClient();
  const chatRef = useRef(chat);
  chatRef.current = chat;
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!landing || chat.draftChatId !== landing.chatId) {
      return;
    }

    const controller = new AbortController();

    async function run(current: Landing) {
      const { parsed, key } = current;
      const image = await resolveFlyoutImage(apolloClient, parsed.imageIds?.[0], controller.signal);
      if (controller.signal.aborted) {
        return;
      }

      onResolvedRef.current(image);

      const { message } = parsed;
      if (!message) {
        return;
      }

      if ((parsed.imageIds?.length && !image) || wasAutoSubmitted(key)) {
        restoreDraft(chatRef.current, message);
        return;
      }

      // Claimed only once the send went out: a busy or failed send leaves the prompt in the
      // composer and the one-shot unclaimed, so a later open can still auto-send it.
      const turn = { sparkId: parsed.sparkId, imageId: image?.imageId };
      const sent = await chatRef.current.sendText(message, turn, sparklinkFileParts(parsed.images));
      // Superseded mid-send: a re-open already cleared the claim, and `chatRef` is the newer chat.
      if (controller.signal.aborted) {
        return;
      }

      if (sent) {
        markAutoSubmitted(key);
        return;
      }
      restoreDraft(chatRef.current, message);
    }

    run(landing)
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        console.error('Failed to send Arty sparklink message:', error);
        restoreDraft(chatRef.current, landing.parsed.message ?? '');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          onDoneRef.current(landing);
        }
      });

    return () => controller.abort();
  }, [landing, chat.draftChatId, apolloClient]);
}
